import { SparklesIcon } from '@heroicons/react/16/solid';
import { Node } from '@tiptap/core';
import { Fragment, type Schema } from '@tiptap/pm/model';
import { Plugin, PluginKey, TextSelection, type Transaction } from '@tiptap/pm/state';
import { NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';

import { CARA_SECTION_ATTRIBUTE, MARKER_PARSE_PRIORITY } from './draft-markers';

export const CARA_SECTION_CAPTION = 'Cara writes this part for each email';
const newSectionInstruction = 'Tell Cara what to write here.';

const sectionBoxClassName =
  // dark-mode-exempt: the section sits on the editor's white paper in both themes.
  'my-3 rounded-md border border-dashed border-neutral-400 bg-neutral-50 p-2.5';
const captionClassName =
  // dark-mode-exempt: on the editor's white paper.
  'flex select-none items-center gap-1.5 text-xs font-semibold text-neutral-600';
const instructionClassName =
  // dark-mode-exempt: on the editor's white paper.
  'mt-1 text-sm text-neutral-600';

const CaraSectionView = () => (
  <NodeViewWrapper className={sectionBoxClassName}>
    <div contentEditable={false} className={captionClassName}>
      <SparklesIcon className="size-4" />
      {CARA_SECTION_CAPTION}
    </div>
    <NodeViewContent className={instructionClassName} />
  </NodeViewWrapper>
);

/** The instruction's words joined by single spaces, the way the server's CaraSections reads them. */
const flattenInstruction = (section: HTMLElement, schema: Schema): Fragment => {
  const words: string[] = [];
  const textNodes = section.ownerDocument.createTreeWalker(section, NodeFilter.SHOW_TEXT);
  while (textNodes.nextNode()) {
    words.push(...(textNodes.currentNode.textContent ?? '').split(/\s+/).filter(Boolean));
  }
  return words.length > 0 ? Fragment.from(schema.text(words.join(' '))) : Fragment.empty;
};

const uniqueSectionIdsKey = new PluginKey('caraSectionUniqueIds');

const selectInstruction = (tr: Transaction, sectionId: string): boolean => {
  tr.doc.descendants((node, pos) => {
    if (node.attrs.sectionId !== sectionId) return true;
    const instructionStart = pos + 1;
    tr.setSelection(
      TextSelection.create(tr.doc, instructionStart, instructionStart + node.content.size)
    );
    return false;
  });
  return true;
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    caraSection: {
      /** Adds a section with a fresh ID and its instruction selected, ready to type over. */
      insertCaraSection: () => ReturnType;
    };
  }
}

/**
 * A part of an email template that Cara writes for each email, stored as
 * `<div data-cara-section="<id>">instruction</div>`. The server reads the instruction as plain
 * words, so the section holds unformatted text.
 */
export const CaraSectionNode = Node.create({
  name: 'caraSection',

  group: 'block',
  content: 'text*',
  marks: '',
  // Backspace and Delete at a section's edges don't merge its instruction with the text around it.
  isolating: true,

  addAttributes() {
    return {
      sectionId: {
        default: null,
        parseHTML: (element) => element.getAttribute(CARA_SECTION_ATTRIBUTE),
        renderHTML: (attributes) => ({ [CARA_SECTION_ATTRIBUTE]: attributes.sectionId })
      }
    };
  },

  parseHTML() {
    return [
      {
        tag: `div[${CARA_SECTION_ATTRIBUTE}]`,
        priority: MARKER_PARSE_PRIORITY,
        // A stored instruction may hold paragraphs or formatting, which plain text can't.
        getContent: (section, schema) => flattenInstruction(section as HTMLElement, schema)
      }
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', HTMLAttributes, 0];
  },

  addCommands() {
    return {
      insertCaraSection:
        () =>
        ({ state, chain }) => {
          const { $from } = state.selection;
          const isInsideSection = $from.parent.type === this.type;
          const isInsideNestedBlock = $from.depth > 1;
          // A section must sit at the top level, so inside a section, a list or a quote the new
          // one goes after that whole block.
          const insertAt =
            isInsideSection || isInsideNestedBlock ? $from.after(1) : state.selection;
          const sectionId = crypto.randomUUID();
          return chain()
            .insertContentAt(insertAt, {
              type: this.name,
              attrs: { sectionId },
              content: [{ type: 'text', text: newSectionInstruction }]
            })
            .command(({ tr }) => selectInstruction(tr, sectionId))
            .run();
        }
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: uniqueSectionIdsKey,
        // A pasted or duplicated section arrives with an ID already in use; the copy, being
        // later in the document, gets a fresh one so each section is still filled on its own.
        appendTransaction: (transactions, _, state) => {
          if (!transactions.some((transaction) => transaction.docChanged)) return null;
          const usedIds = new Set<string>();
          const { tr } = state;
          state.doc.descendants((node, pos) => {
            if (node.type !== this.type) return true;
            const { sectionId } = node.attrs;
            if (usedIds.has(sectionId)) {
              tr.setNodeAttribute(pos, 'sectionId', crypto.randomUUID());
            } else {
              usedIds.add(sectionId);
            }
            return false;
          });
          return tr.docChanged ? tr : null;
        }
      })
    ];
  },

  addKeyboardShortcuts() {
    return {
      // Splitting a section would give both halves its ID, so Enter only works at the end,
      // where it starts a paragraph below.
      Enter: ({ editor }) => {
        const { $from } = editor.state.selection;
        if ($from.parent.type !== this.type) return false;
        const isAtSectionEnd = $from.parentOffset === $from.parent.content.size;
        if (!isAtSectionEnd) return true;
        const afterSection = $from.after();
        return editor
          .chain()
          .insertContentAt(afterSection, { type: 'paragraph' })
          .setTextSelection(afterSection + 1)
          .run();
      },
      // Isolating keeps an emptied section from merging away, so Backspace removes it instead.
      Backspace: ({ editor }) => {
        const { $from, empty } = editor.state.selection;
        const isEmptySection = $from.parent.type === this.type && $from.parent.content.size === 0;
        return empty && isEmptySection && editor.commands.deleteNode(this.name);
      }
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(CaraSectionView);
  }
});
